import { useMutation } from "@apollo/client";
import {
  Button,
  Flexbox,
  Icons,
  Label,
  Loading,
  Modal,
  Text,
  Variants
} from "@sede-x/shell-ds-react-framework";
import { loader } from "graphql.macro";
import { useContext, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import { GqlResponse, TBatch, TMovement } from "ticketing/ticketing.types";
import { equalsIgnoreCase } from "ticketing/utils";
import "./movements.css";
import RevisionsBatch from "./RevisionsBatch";
import { MovementsMainContext } from "./utils";
export type BatchComponentProps = {
  batch: TBatch;
  isSelected: boolean;
};

const UPDATE_BATCH_TRANSIT_COMPLETE = loader(
  "../../ticketing-graphql/updateBatchTransitComplete.graphql"
);

const MOVEMENT_STATUS_ACTUALIZED = "actualized";
const MOVEMENT_STATUS_CANCELLED = "Cancelled";
const SERVICE_PROVIDER_GAIN_LOSS = "gain/loss";
const ICON_STYLE = { fill: "var(--primary-icon-color)" };

/**
 * @dev
 * the biz rules are -
 * 1.   show the icon if transitDealNum > 0
 * 2.   if transitComplete show the lock icon, else the unlock
 * 08/20/2022
 * 3. 1365633 - Transit Complete Validation: Do not allow action when unactualized movements exist
 */
type Response = GqlResponse<TBatch, "updateTransitComplte">;
export default function BatchComponent({ batch, isSelected }: Readonly<BatchComponentProps>) {
  const { onBatchSelected, onBatchUpdated } = useContext(MovementsMainContext);
  const [updateBatch, { loading, error }] = useMutation<Response>(
    UPDATE_BATCH_TRANSIT_COMPLETE,
    {
      onCompleted: data => onBatchUpdated?.(data.updateTransitComplte)
    }
  );

  const [showBatchRevisions, setShowBatchRevisions] = useState(false);
  const [showTransitCompleteWarningMessage, setShowTransitCompleteWarningMessage] =
    useState(false);

  const handleCloseRevisions = () => {
    setShowBatchRevisions(false);
  };

  const updateBatchToggleTransitComplete = () => {
    updateBatch({
      variables: {
        batch: {
          id: batch.id,
          version: batch.version,
          transitComplete: !batch.transitComplete
        }
      }
    });
  };

  const onConfirmMarkTransitComplete = () => {
    setShowTransitCompleteWarningMessage(false);
    updateBatchToggleTransitComplete();
  };
  const onCancelTransitComplete = () => {
    setShowTransitCompleteWarningMessage(false);
  };

  const shouldShowTransitCompleteWarning =
    batch?.movements?.some(m => m.isActualizedExternally) && !batch.transitComplete;

  const handleToggleTransitComplete = () => {
    //2765307
    if (shouldShowTransitCompleteWarning) {
      setShowTransitCompleteWarningMessage(true);
    } else {
      updateBatchToggleTransitComplete();
    }
  };

  const handleBatchSelected = () => {
    if (!isSelected) {
      onBatchSelected?.(batch);
    }
  };

  const isValidMovement = (m: Partial<TMovement>) => {
    const isActualizedOrCancelled =
      equalsIgnoreCase(m?.status?.name, MOVEMENT_STATUS_ACTUALIZED) ||
      equalsIgnoreCase(m?.status?.name, MOVEMENT_STATUS_CANCELLED);

    const hasTicketsOrZeroQuantity =
      (m.tickets?.length ?? 0) > 0 || Math.round(m.scheduledQuantity ?? 0) === 0.0;

    const isGainLossProvider = equalsIgnoreCase(
      m?.activityType?.serviceProvider,
      SERVICE_PROVIDER_GAIN_LOSS
    );

    return isActualizedOrCancelled || hasTicketsOrZeroQuantity || isGainLossProvider;
  };

  const shouldDisableTransitComplete = () => {
    if (batch.movements && batch.movements.length > 0) {
      //disable transit complete if there is any invalid movement
      return batch.movements.filter(m => !isValidMovement(m)).length > 0;
    }
    //disable if no valid movements - possible if all movements have been cancelled
    return true;
  };

  const hideTransitComplete = (batch.transitDealNum ?? 0) <= 0;
  const className = isSelected ? "selected" : "";
  const logisticsSystemColor = isSelected ? "red" : "#ED3EFA";
  const batchNameColor = isSelected ? "rgb(255, 198, 0)" : "";
  const logisticsSystemData =
    batch?.transport?.at(0)?.carriages?.[0]?.name && isSelected
      ? `${batch?.logisticsSystem?.name} (${batch?.transport?.at(0)?.carriages[0]?.name})`
      : batch?.logisticsSystem?.name;

  return (
    <>
      {showBatchRevisions && (
        <RevisionsBatch batchId={batch.id} onClose={handleCloseRevisions}></RevisionsBatch>
      )}
      <div
        className={`${className} movement-batch-comp-outter batch-item`}
        onClick={handleBatchSelected}
        role="none">
        <div className="movement-batch-comp-wrapper">
          <div className="movement-batch-comp-label-wrapper">
            <Flexbox alignItems="flex-start" gap="0px">
              <Label size="small" bold style={{ color: `${batchNameColor}` }}>
                {batch?.name}
              </Label>
              <Button
                variant={Variants.Transparent}
                iconPosition="right"
                icon={
                  <Icons.CopyFile
                    className="copy-btn"
                    style={{ fill: "#ff8c00", width: "20px", height: "20px" }}
                  />
                }
                className="batch-icon-button"
                onClick={e => {
                  navigator.clipboard.writeText(batch?.name);
                  e.stopPropagation();
                }}
              />
            </Flexbox>
            <Label size="small" bold style={{ color: `${logisticsSystemColor}` }}>
              {logisticsSystemData}
            </Label>
          </div>
          {isSelected && (
            <div className="movement-batch-comp-btn-wrap">
              <Button
                icon={
                  batch?.transitComplete ? (
                    <Icons.Lock style={ICON_STYLE} />
                  ) : (
                    <Icons.Unlock style={ICON_STYLE} />
                  )
                }
                disabled={shouldDisableTransitComplete()}
                className="batch-icon-button"
                title="Toggle transit complete"
                onClick={handleToggleTransitComplete}
                hidden={hideTransitComplete}></Button>
              <Button
                icon={<Icons.Time style={ICON_STYLE} />}
                className="batch-icon-button"
                title="View Revisions"
                onClick={() => setShowBatchRevisions(true)}></Button>
            </div>
          )}
        </div>
        {loading && <Loading size="small" />}
        {error && <ApolloErrorViewer error={error} />}
        {showTransitCompleteWarningMessage && (
          <Modal
            title={"Transit Complete"}
            open={showTransitCompleteWarningMessage}
            onClose={onCancelTransitComplete}
            actions={[
              {
                label: "Cancel",
                action: onCancelTransitComplete,
                props: {
                  variant: Variants.Outlined,
                  icon: <Icons.Cross />
                }
              },
              {
                label: "Confirm",
                action: onConfirmMarkTransitComplete,
                props: {
                  icon: <Icons.Check />
                }
              }
            ]}>
            <Text>
              This batch has nominations that have been actualized externally. Please confirm
              setting Transit Complete?
            </Text>
          </Modal>
        )}
      </div>
    </>
  );
}
